'use client';

import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/design-system/table';
import { cn } from '@/lib/utils';

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  searchPlaceholder?: string;
  showToolbar?: boolean;
  showViewOptions?: boolean;
  showPagination?: boolean;
  pageSize?: number;
  className?: string;
}

export function NormalTable<TData, TValue>({ columns, data, className }: DataTableProps<TData, TValue>) {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const borderClass = 'border border-[gray]';
  const cellBorderClass = 'border-r border-r-secondary last:border-r-0 font-bold ';

  return (
    <div className={cn('space-y-4', className)}>
      <div className={cn('rounded-sm border-[grey] overflow-hidden', borderClass)}>
        <Table className={cn('rounded-sm')}>
          <TableHeader className="bg-background text-xs font-bold">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="border-b border-b-secondary">
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id} className={cellBorderClass}>
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length > 0 ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                  className="border-b border-b-gray-50 "
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className={cn(
                        cellBorderClass,
                        cell.column.id === 'id'
                          ? 'bg-background text-xs font-bold border-t last:text-right'
                          : 'bg-card text-xs font-medium last:text-right',
                      )}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
